import type { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { normalizeKeyInitiativeStatus } from 'partner360/entities/keyInitiatives/model/status'
import { buildBusinessPriorityUidToNameMap } from 'partner360/features/mdfActivities/helpers/activityBusinessPriorities'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import type {
  CockpitPeriodKey,
  ICockpitKeyInitiative,
  ICockpitPlanningPeriod,
} from '../../model/types'

import { createCockpitBuStrategicInitiativeResolver } from './getCockpitBuStrategicInitiative'
import {
  getInitiativePeriodAllocation,
  getInitiativeQuarters,
  getSelectedCockpitQuarters,
} from './getCockpitInitiativesInScope'
import {
  getInitiativeMdfPlannedAmount,
  getInitiativeMetricAmount,
  isSupportedInitiativeMetric,
} from './getInitiativeMetricAmount'
import type {
  ICockpitInitiativeGroup,
  ICockpitInitiativeRow,
  TCockpitInitiativeGroupField,
  TCockpitInitiativeValueMode,
} from './types'

export const EMPTY_GROUP_LABEL = '—'

interface IRowsParams {
  initiatives: ICockpitKeyInitiative[]
  kiBusinessPriorities: IKIBusinessPriority[]
  selectedMetric: ActualsValueTypes
  isLocalCurrency: boolean
  selectedPeriods: CockpitPeriodKey[]
  planningPeriod: ICockpitPlanningPeriod | null
  keyObjectives: IKeyObjective[]
}

/**
 * Turns the in-scope initiatives into the rows the tile's list renders: amounts resolved to the
 * selected measure/currency and business-priority uids resolved to names. Unresolvable amounts
 * stay `null` so the formatters render `--` instead of a zero the partner never planned.
 *
 * An initiative states its period as the quarters it spans (`"Q1,Q2,Q4"`), and the list shows
 * as a joined value. Each initiative stays one row whose amounts contain only the selected
 * quarters' equal shares. Its selected quarters are kept alongside the row so only the Period
 * grouping can expand that selected total into individual quarter rows.
 */
export const getCockpitInitiativeRows = ({
  initiatives,
  kiBusinessPriorities,
  selectedMetric,
  isLocalCurrency,
  selectedPeriods,
  planningPeriod,
  keyObjectives,
}: IRowsParams): ICockpitInitiativeRow[] => {
  const uidToName = buildBusinessPriorityUidToNameMap(kiBusinessPriorities)
  const isSupportedMetric = isSupportedInitiativeMetric(selectedMetric)
  const selectedQuarters = getSelectedCockpitQuarters(selectedPeriods, planningPeriod?.fQuarter)
  const resolveBuStrategicInitiative = createCockpitBuStrategicInitiativeResolver(keyObjectives)
  const getAmount = (initiative: ICockpitKeyInitiative, valueType: 'Goal' | 'Incremental') =>
    isSupportedMetric
      ? getInitiativeMetricAmount(initiative, valueType, selectedMetric, isLocalCurrency)
      : null

  return initiatives.map((initiative) => {
    const initiativeQuarters = getInitiativeQuarters(initiative.Period)
    const quartersInScope =
      selectedQuarters === null
        ? initiativeQuarters
        : initiativeQuarters.filter((quarter) => selectedQuarters.includes(quarter))
    const amountAllocation = getInitiativePeriodAllocation(
      initiative.Period,
      selectedPeriods,
      planningPeriod?.fQuarter
    )
    const allocateAmount = (amount: number | null) =>
      amount === null ? null : amount * amountAllocation

    const businessPriorities = (initiative.BusinessPriorities ?? '')
      .split(',')
      .map((uid) => uid.trim().toLocaleLowerCase())
      .filter(Boolean)
      .map((uid) => uidToName.get(uid))
      .filter((name): name is string => Boolean(name))
    const rawDevelopment = initiative.Development?.trim()
    const buStrategicInitiative = rawDevelopment
      ? resolveBuStrategicInitiative(rawDevelopment, initiative.ProgramType)
      : null

    return {
      entryId: initiative.EntryId,
      partnerId: initiative.PartnerId,
      name: initiative.Name || EMPTY_GROUP_LABEL,
      development: buStrategicInitiative?.root || EMPTY_GROUP_LABEL,
      buStrategicInitiativeLeaf: buStrategicInitiative?.leaf || EMPTY_GROUP_LABEL,
      buStrategicInitiativeTips: buStrategicInitiative ? [buStrategicInitiative.breadcrumb] : [],
      buStrategicInitiativeRootOrder: buStrategicInitiative?.rootOrder ?? Number.MAX_SAFE_INTEGER,
      buStrategicInitiativeLeafOrder: buStrategicInitiative?.leafOrder ?? Number.MAX_SAFE_INTEGER,
      businessPriorities,
      offering: initiative.ProgramType?.trim() || EMPTY_GROUP_LABEL,
      category: initiative.BusinessUnitTranslation?.trim() || EMPTY_GROUP_LABEL,
      period: initiative.Period || EMPTY_GROUP_LABEL,
      periodsInScope: quartersInScope.length
        ? quartersInScope.map((quarter) => `Q${quarter}`)
        : [EMPTY_GROUP_LABEL],
      status: normalizeKeyInitiativeStatus(initiative.Status) || EMPTY_GROUP_LABEL,
      goal: allocateAmount(getAmount(initiative, 'Goal')),
      incremental: allocateAmount(getAmount(initiative, 'Incremental')),
      mdfPlanned: allocateAmount(getInitiativeMdfPlannedAmount(initiative, isLocalCurrency)),
    }
  })
}

/** Group labels a row belongs to. Only business priorities can be several — an initiative
 *  declaring two GTM priorities is counted under both, the way the MDF split is. */
const getRowGroupNames = (
  row: ICockpitInitiativeRow,
  groupField: TCockpitInitiativeGroupField
): string[] => {
  switch (groupField) {
    case 'businessPriority':
      return row.businessPriorities.length ? row.businessPriorities : [EMPTY_GROUP_LABEL]
    case 'category':
      return [row.category]
    case 'period':
      return [row.period]
    case 'status':
      return [row.status]
    case 'development':
      return [row.development]
  }
}

/** Null-preserving sum: a group of initiatives that all lack the measure stays `--`, while one
 *  real amount among them makes the total real. */
export const sumCockpitInitiativeAmounts = (values: (number | null)[]): number | null =>
  values.reduce<number | null>((sum, value) => (value === null ? sum : (sum ?? 0) + value), null)

/** Period is the only grouping where one initiative becomes one row per selected quarter. The
 *  base row already contains the selected quarters' total, so dividing by their count restores
 *  the equal per-quarter amount for each emitted row. */
const expandRowsByPeriod = <TRow extends ICockpitInitiativeRow>(rows: TRow[]): TRow[] =>
  rows.flatMap((row) => {
    const quarterCount = row.periodsInScope.length || 1
    const share = (value: number | null) => (value === null ? null : value / quarterCount)

    return row.periodsInScope.map(
      (period) =>
        ({
          ...row,
          period,
          goal: share(row.goal),
          incremental: share(row.incremental),
          mdfPlanned: share(row.mdfPlanned),
        }) as TRow
    )
  })

/**
 * Buckets the initiative rows by the selected group field and sorts them by the requested value.
 */
export const getCockpitInitiativeGroups = <TRow extends ICockpitInitiativeRow>(
  rows: TRow[],
  groupField: TCockpitInitiativeGroupField,
  valueMode: TCockpitInitiativeValueMode
): ICockpitInitiativeGroup<TRow>[] => {
  const groups = new Map<string, TRow[]>()
  const rowsToGroup = groupField === 'period' ? expandRowsByPeriod(rows) : rows

  rowsToGroup.forEach((row) => {
    getRowGroupNames(row, groupField).forEach((name) => {
      groups.set(name, [...(groups.get(name) ?? []), row])
    })
  })

  const built = Array.from(groups.entries()).map(([name, groupRows]) => ({
    name,
    rows: groupRows,
    goal: sumCockpitInitiativeAmounts(groupRows.map(({ goal }) => goal)),
    incremental: sumCockpitInitiativeAmounts(groupRows.map(({ incremental }) => incremental)),
    mdfPlanned: sumCockpitInitiativeAmounts(groupRows.map(({ mdfPlanned }) => mdfPlanned)),
    buStrategicInitiativeTips:
      groupField === 'development'
        ? Array.from(
            new Set(groupRows.flatMap(({ buStrategicInitiativeTips }) => buStrategicInitiativeTips))
          ).sort((left, right) => left.localeCompare(right))
        : [],
  }))

  if (groupField === 'period') {
    return built.sort((left, right) => left.name.localeCompare(right.name))
  }

  const compareByValue = (left: (typeof built)[number], right: (typeof built)[number]) =>
    getGroupValue(right, valueMode) - getGroupValue(left, valueMode)

  if (groupField !== 'development') {
    return built.sort(compareByValue)
  }

  return built.sort((left, right) => {
    const getRootOrder = (group: (typeof built)[number]) =>
      Math.min(
        ...group.rows.map(({ buStrategicInitiativeRootOrder }) => buStrategicInitiativeRootOrder)
      )
    const orderDifference = getRootOrder(left) - getRootOrder(right)

    return orderDifference || compareByValue(left, right)
  })
}

/** The sortable value of a group for the requested mode. */
export const getGroupValue = (
  group: ICockpitInitiativeGroup,
  valueMode: TCockpitInitiativeValueMode
): number => {
  if (valueMode === 'count') {
    return group.rows.length
  }

  return (valueMode === 'incremental' ? group.incremental : group.goal) ?? 0
}
